import { Handle, Position, type NodeProps } from "@xyflow/react";
import { Play, Mail, Hash, Send, MailCheck } from "lucide-react";
import { cn } from "@/lib/utils";
import { runNodeBadgeClasses, runNodeBadgeLabel, runNodeDotClasses, runNodeFrameClasses, runNodeGlowClasses } from "@/components/workflows/node-theme";

const ACTION_LABELS: Record<string, { label: string; icon: typeof Play }> = {
  http_post: { label: "HTTP POST", icon: Send },
  send_email: { label: "Send email", icon: Mail },
  wait_for_email_reply: { label: "Wait for email reply", icon: MailCheck },
  post_to_channel: { label: "Post to channel", icon: Hash },
};

/**
 * Action node — generic side effect (HTTP POST, email, channel post). One
 * target (left), no source handle in the design-only MVP. Purple accent.
 */
export function ActionNode({ data, selected }: NodeProps) {
  const d = data as { label?: string; actionType?: string; description?: string; runStatus?: string; runSelected?: boolean };
  const action = d.actionType ? ACTION_LABELS[d.actionType] : undefined;
  const canContinue = d.actionType === "wait_for_email_reply";
  const Icon = action?.icon ?? Play;
  const runFrame = runNodeFrameClasses(d.runStatus);
  const runDot = runNodeDotClasses(d.runStatus);
  const runGlow = runNodeGlowClasses(d.runStatus);
  const runBadge = runNodeBadgeLabel(d.runStatus);
  const runBadgeClasses = runNodeBadgeClasses(d.runStatus);
  return (
    <div
      className={cn(
        "relative w-[240px] rounded-xl border bg-surface shadow-sm transition-all duration-300",
        runFrame ?? (selected ? "border-purple-500 shadow-purple-500/20" : "border-border"),
        d.runSelected && "outline outline-2 outline-offset-4 outline-accent/70",
      )}
    >
      {runGlow && (
        <span className={cn("pointer-events-none absolute -inset-3 z-0 rounded-[1.35rem]", runGlow)} />
      )}
      {runDot && (
        <span className={cn("absolute right-2 top-2 z-10 h-2.5 w-2.5 rounded-full ring-2 ring-surface", runDot)} />
      )}
      {runBadge && runBadgeClasses && (
        <span className={cn("absolute -right-2 -top-3 z-20 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide", runBadgeClasses)}>
          {runBadge}
        </span>
      )}
      <div className="flex items-center gap-1.5 bg-purple-500/10 px-3 py-1.5 text-[11px] font-medium uppercase tracking-wide text-purple-500">
        <Play size={11} />
        Action
      </div>
      <div className="flex items-start gap-2.5 px-3 py-2.5">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-purple-500/10 text-purple-500">
          <Icon size={14} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[13px] font-semibold text-text-primary">
            {d.label || action?.label || "Action"}
          </p>
          <p className="mt-0.5 line-clamp-2 text-[11px] text-text-muted">
            {d.description || "Configure the action"}
          </p>
        </div>
      </div>
      <Handle
        type="target"
        position={Position.Left}
        className="!h-2.5 !w-2.5 !border-2 !border-surface !bg-purple-500"
      />
      {canContinue && (
        <Handle
          type="source"
          position={Position.Right}
          className="!h-2.5 !w-2.5 !border-2 !border-surface !bg-purple-500"
        />
      )}
    </div>
  );
}
